import React from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import { formatNull, formatDateTime, getNameForImportantEventsType } from '../../Utils/GlobalUtils';

import Modal from '../../Components/Modal';
import FilterImportantEvents from '../FilterComponents/FilterImportantEvents';

import LoadingLines from '../../Components/LoadingLines';
import WarningPanel from '../WarningPanel';
import Loading from '../Loading';

import IconContainer from '../../Components/IconContainer';
import { ReactComponent as ReloadIcon } from '../../icons/reload.svg';
import { ReactComponent as FunnelIcon } from '../../icons/funnel-bold.svg';
import { ReactComponent as FunnelFillIcon } from '../../icons/funnel-fill.svg';

export default function ImportantEvents() {
    const navigate = useNavigate();
    const [entries, setEntries] = React.useState([]);

    const [limitForView, setLimitForView]= React.useState(50);
    const [warning, setWarning] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [reload, setReload] = React.useState(true);

    const [lastUpdateTime, setLastUpdateTime] = React.useState('');

    const DEFAULT_FILTERS = {
        type: '',
    };

    const STORAGE_KEY = 'important_events_filters';

    const [filters, setFilters] = React.useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? { ...DEFAULT_FILTERS, ...JSON.parse(saved) } : DEFAULT_FILTERS;
        } catch {
            return DEFAULT_FILTERS;
        }
    });

    React.useEffect(() => {
        setLoading(true);

        const token = localStorage.getItem('token');
        axios.get(`/api/get-all-important-events?limitForView=${limitForView}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then(response => {
            setEntries(response.data);
        })
        .catch((error) => {
            console.error(error);
            setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
        })
        .finally(() => {
            setReload(false);
            setLoading(false);
            setLastUpdateTime(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
        })
    }, [reload]);

    const hasActiveFilters = Object.entries(filters).some(([key, value]) => 
        Array.isArray(value) ? value.length > 0 : Boolean(value)
    );

    React.useEffect(() => {
        const token = localStorage.getItem('token');
        setLoading(true);

        if (!hasActiveFilters) {
            axios.get(`/api/get-all-important-events?limitForView=${limitForView}`, {
                headers: { 
                    Authorization: `Bearer ${token}` 
                }
            }).then((response) => {
                setWarning('');
                setEntries(response.data);
            })
            .catch((error) => {
                console.error(error);
                setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
            })
            .finally(() => {
                setReload(false);
                setLoading(false);
                setLastUpdateTime(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
            });
        } else {
            axios.post('/api/get-all-important-events-filtered', filters, {
                headers: { 
                    Authorization: `Bearer ${token}` 
                },
                params: { 
                    limitForView 
                },
            })
            .then((response) => {
                setWarning('');
                setEntries(response.data);
            })
            .catch((error) => {
                console.error(error);
                setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
            })
            .finally(() => {
                setReload(false);
                setLoading(false);
                setLastUpdateTime(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
            });
        }
    }, [reload]);

    const [isFilter, setIsFilter] = React.useState(false);
    const [fieldFilter, setFieldFilter] = React.useState(null);

    React.useEffect(() => {
        if (window.location.hash === '#filter') {
            setIsFilter(true);
        }
    }, []);

    return (
    <>
        <Modal isOpen={isFilter} closeModal={() => { setIsFilter(false); setFieldFilter('all'); setReload(true) }}>
            <FilterImportantEvents field={fieldFilter} filters={filters} setFilters={setFilters} DEFAULT_FILTERS={DEFAULT_FILTERS} />
        </Modal>

        <div className='pageWrapper'>
            <div className='tableContainer'>
                <div className='tableHeaderWrapper'>
                    <div className='tableStatus'>
                        <h3></h3>
                        <a className='lastUpdateWrapper'>
                            Последнее обновление таблицы: {lastUpdateTime || '--:--'} 
                            {loading ? 
                                <Loading type='inline' /> 
                            : 
                                <IconContainer Icon={ReloadIcon} onClick={() => setReload(true)} size = '14' color = 'black' className = '' />
                            }
                        </a>
                    </div>
                    <div className='tableHeader' style={{gridTemplateColumns: '20fr 25fr 15fr 20fr 10fr'}}>
                        <div>
                            ФИО и email пользователя
                        </div>
                        <div>
                            ФИО и email инициатора события
                        </div>
                        <div>
                            <p>Тип события</p>
                            <IconContainer 
                                Icon={filters.type ? FunnelFillIcon : FunnelIcon} 
                                onClick={() => {
                                    setIsFilter(true);
                                    setFieldFilter('type');
                                }} 
                                size = '15' 
                                color = 'black' 
                                className = {`
                                    ${'filterHeaderIcon'} 
                                    ${filters.type && 'showedFilterHeaderIcon'}
                                `}
                            />
                        </div>
                        <div>Примечание</div>
                        <div>Дата и время</div>
                    </div>
                </div>
                {(entries.length===0 && loading) ? <LoadingLines />
                : warning ? <WarningPanel text={warning} inTable={true} buttonText='Повторить попытку' buttonAction={() => setReload(true)} />
                : entries.map((entry) => (
                    <motion.div
                        className='tableRow nonActive' 
                        key={entry.id}
                        initial={{ scale: '97%', opacity: 0 }}
                        animate={{ scale: '100%', opacity: 1 }}
                        exit={{ scale: '97%', opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ gridTemplateColumns: '20fr 25fr 15fr 20fr 10fr' }}
                    >
                        <div>
                            {entry.user_lastname ? (
                                <Link 
                                    className='aWithLink' 
                                    to={`/account/${entry.user_id}`}
                                >
                                    {entry.user_lastname} {entry.user_firstname} {entry.user_surname} {entry.user_email}
                                </Link>
                            ) : (
                                formatNull('')
                            )}
                        </div>
                        <div>
                            {entry.initiator_email ?
                            <Link 
                                className='aWithLink' 
                                to={`/account/${entry.initiator_id}`}
                            >
                                {entry.initiator_lastname} {entry.initiator_firstname} {entry.initiator_surname} {entry.initiator_email}
                            </Link>
                            :
                            <p className='description'>Инициатором является система</p>}
                        </div>
                        <div>{getNameForImportantEventsType(entry.type)}</div>
                        <div>{formatNull(entry.note)}</div>
                        <div
                            className='monoTime'
                        >
                            {formatDateTime(entry.created_at)}
                        </div>
                    </motion.div>
                ))}
                {!warning && entries.length > 0 && (entries.length < entries[0].total_count) && (
                    <div className='tableLoadMoreRow' style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                        <div></div>
                        <div>
                            <button
                                type='button'
                                className='button'
                                style={{ width: 'fit-content' }}
                                onClick={() => {setLimitForView(prev => prev + 50); setReload(true)}}
                                disabled={loading}
                            >
                                {loading ? <Loading type='inline' /> : 'Загрузить ещё'}
                            </button>
                        </div>
                        <div></div>
                    </div>
                )}
            </div>
        </div>
    </>
    )
}
